import { cn } from '@/lib/utils'
import type { TestResult } from '../execution-context'
import { ClassOperationsTable } from './class-operations-table'
import { FailurePanel, runtimeFailureFor } from './failure-panel'
import { SectionLabel } from './section-label'
import { stringifyValue, unwrapInputMarker } from './value-format'

/**
 * Every param row on the card goes through here, so the "Input" and "Your
 * Input After the Call" sections stay in one representation — see
 * unwrapInputMarker for why that matters.
 */
function ParamRows({ params }: { params: Record<string, unknown> }) {
  return (
    <div className="bg-muted/50 space-y-1 overflow-x-auto rounded p-2">
      {Object.entries(params).map(([key, value]) => (
        <div key={key} className="font-mono text-sm">
          <span className="text-blue-600 dark:text-blue-400">{key}</span>
          <span className="text-muted-foreground"> = </span>
          <span className="text-foreground">
            {stringifyValue(unwrapInputMarker(value))}
          </span>
        </div>
      ))}
    </div>
  )
}

function TestCaseInputs({ inputs }: { inputs: Record<string, unknown> }) {
  if (Object.keys(inputs).length === 0) {
    return null
  }

  return (
    <div className="space-y-1">
      <SectionLabel>Input</SectionLabel>
      <ParamRows params={inputs} />
    </div>
  )
}

/**
 * Post-call state of the input params, shown only on a failing custom-verify
 * case (LG-561). When the return value ALSO matches, this section is the only
 * thing on the card explaining the verdict — hence the note.
 */
function TestCaseInputsAfter({
  inputsAfter,
  returnValueMatches,
}: {
  inputsAfter: Record<string, unknown>
  returnValueMatches: boolean
}) {
  if (Object.keys(inputsAfter).length === 0) {
    return null
  }

  return (
    <div className="space-y-1">
      <SectionLabel>Your Input After the Call</SectionLabel>
      <ParamRows params={inputsAfter} />
      {/* Always frame the section: unlabeled, a second block of params reads
          as noise. The matching-return case gets the stronger wording because
          there the card is otherwise identical on both sides. */}
      <p className="text-muted-foreground text-sm">
        {returnValueMatches
          ? 'Your return value matches, but this problem also checks the contents of the input after your function runs (e.g. in-place modification).'
          : 'This problem also checks the contents of the input after your function runs (e.g. in-place modification).'}
      </p>
    </div>
  )
}

function TestCaseIntent({ intent }: { intent: string }) {
  return (
    <div className="space-y-1">
      <SectionLabel>What This Tests</SectionLabel>
      <div className="rounded border border-blue-500/20 bg-blue-500/5 p-2 text-sm text-gray-800 dark:text-gray-200">
        {intent}
      </div>
    </div>
  )
}

interface CaseDetailProps {
  result: TestResult
}

/**
 * Body of a single test case: Input, Intent, then either the runtime-error
 * panel, the class operations table, or Expected vs Your Output.
 * Shared by the Run per-case tabs and the Submit first-failure card.
 */
export function CaseDetail({ result }: CaseDetailProps) {
  const isClassBased = (result.operations?.length ?? 0) > 0
  const runtimeFailure = runtimeFailureFor(result)

  return (
    <div className="space-y-3">
      {/* Input: authored override wins over raw key = value rows */}
      {result.inputDisplayOverride ? (
        <div className="space-y-1">
          <SectionLabel>Input</SectionLabel>
          <pre className="bg-muted/50 overflow-x-auto rounded p-2 font-mono text-sm text-foreground whitespace-pre-wrap">
            {result.inputDisplayOverride}
          </pre>
        </div>
      ) : (
        result.inputs && <TestCaseInputs inputs={result.inputs} />
      )}

      {/* Intent - why this test case exists */}
      {result.intent && <TestCaseIntent intent={result.intent} />}

      {isClassBased ? (
        <>
          {runtimeFailure && <FailurePanel failure={runtimeFailure} />}
          <ClassOperationsTable operations={result.operations ?? []} />
        </>
      ) : runtimeFailure ? (
        <FailurePanel failure={runtimeFailure} />
      ) : (
        <>
          {/* Authored formatted expected output, when present */}
          {result.outputDisplayOverride && (
            <div className="space-y-1">
              <SectionLabel>Expected (Formatted)</SectionLabel>
              <pre className="bg-muted/50 overflow-x-auto rounded p-2 font-mono text-sm text-foreground whitespace-pre-wrap">
                {result.outputDisplayOverride}
              </pre>
            </div>
          )}

          {/* Expected */}
          <div className="space-y-1">
            <SectionLabel>Expected</SectionLabel>
            <pre className="bg-muted/50 overflow-x-auto rounded p-2 font-mono text-sm text-foreground">
              {stringifyValue(result.expected)}
            </pre>
          </div>

          {/* Actual */}
          <div className="space-y-1">
            <SectionLabel>Your Output</SectionLabel>
            <pre
              className={cn(
                'overflow-x-auto rounded p-2 font-mono text-sm',
                result.passed
                  ? 'bg-muted/50 text-foreground'
                  : 'bg-red-500/10 text-red-700 dark:text-red-300',
              )}
            >
              {stringifyValue(result.actual)}
            </pre>
          </div>

          {/* Why a custom verifier rejected a matching return value */}
          {result.inputsAfter && (
            <TestCaseInputsAfter
              inputsAfter={result.inputsAfter}
              returnValueMatches={
                stringifyValue(result.actual) ===
                stringifyValue(result.expected)
              }
            />
          )}
        </>
      )}
    </div>
  )
}
